export const EVERYONE_OPTION_ID = "circle";
export const INTERNAL_TEAM_OPTION_ID = "internal_team";
export const INTERNAL_TEAM_ONLY_LABEL = "Internal team only";

const STAFF_ONLY_FORM_PATCH = {
  publishMode: "now" as const,
  publishAt: "",
  eventReminderModes: [] as [],
  eventReminderMode: "off" as const,
  isUrgent: false as const,
};

export type NoticeAudienceWire = {
  audience?: string;
  targetSubcircle?: string;
  staffOnly?: boolean;
};

export type NoticeAudienceCatalog = {
  organizationName?: string;
  organizationLogo?: string;
  canChooseEveryone: boolean;
  circles: ReadonlyArray<{
    circleId: string;
    name: string;
    logo?: string;
  }>;
};

export type NoticeAudienceOption = {
  id: string;
  label: string;
  subtitle: string;
  avatarText: string;
  avatarSrc?: string;
  selected: boolean;
};

export type NoticeAudienceSettleMode = "review" | "mobileInfo" | "mobileChips";

type FormChangeHandler = (event: {
  target: { name: string; value: unknown };
}) => void;

export function noticeAudienceInitials(value: string): string {
  return (
    value
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || "")
      .join("") || "?"
  );
}

export function everyoneAudienceLabel(organizationName?: string): string {
  const name = organizationName?.trim();
  return name || "Entire Circle";
}

export function listNoticeAudienceOptions(input: {
  catalog: NoticeAudienceCatalog;
  wire: NoticeAudienceWire;
  includeInternalTeam?: boolean;
  /** Review uses "All members"; mobile Information cards use "". */
  circleSubtitle?: string;
}): NoticeAudienceOption[] {
  const { catalog, wire } = input;
  const circleSubtitle = input.circleSubtitle ?? "All members";
  const staffOnly = wire.staffOnly === true;
  const staffBlocksMembers = Boolean(input.includeInternalTeam) && staffOnly;
  const everyoneLabel = everyoneAudienceLabel(catalog.organizationName);
  const options: NoticeAudienceOption[] = [];

  if (input.includeInternalTeam) {
    options.push({
      id: INTERNAL_TEAM_OPTION_ID,
      label: INTERNAL_TEAM_ONLY_LABEL,
      subtitle: "Staff only",
      avatarText: "T",
      selected: staffOnly,
    });
  }

  if (catalog.canChooseEveryone) {
    options.push({
      id: EVERYONE_OPTION_ID,
      label: everyoneLabel,
      subtitle: "Everyone",
      avatarText: noticeAudienceInitials(everyoneLabel),
      avatarSrc: catalog.organizationLogo,
      selected: !staffBlocksMembers && wire.audience === "circle",
    });
  }

  catalog.circles.forEach((circle) => {
    options.push({
      id: circle.circleId,
      label: circle.name,
      subtitle: circleSubtitle,
      avatarText: noticeAudienceInitials(circle.name),
      avatarSrc: circle.logo,
      selected:
        !staffBlocksMembers &&
        wire.audience === "subcircle" &&
        wire.targetSubcircle === circle.circleId,
    });
  });

  return options;
}

export function listMobileChipAudiences(
  catalog: NoticeAudienceCatalog,
): Array<{ id: string; label: string }> {
  const items: Array<{ id: string; label: string }> = [];
  if (catalog.canChooseEveryone) {
    items.push({ id: EVERYONE_OPTION_ID, label: "All audiences" });
  }
  catalog.circles.forEach((circle) => {
    items.push({ id: circle.circleId, label: circle.name });
  });
  return items;
}

export function selectedMobileChipAudienceId(
  wire: NoticeAudienceWire,
  canChooseEveryone: boolean,
): string {
  if (wire.audience === "circle" && canChooseEveryone) {
    return EVERYONE_OPTION_ID;
  }
  return wire.targetSubcircle || "";
}

export function noticeAudiencePickPatch(
  optionId: string,
  options?: { includeStaffOnly?: boolean },
): Record<string, unknown> {
  const includeStaffOnly = options?.includeStaffOnly === true;

  if (optionId === INTERNAL_TEAM_OPTION_ID) {
    return {
      ...(includeStaffOnly ? { staffOnly: true } : {}),
      ...STAFF_ONLY_FORM_PATCH,
    };
  }

  const audiencePatch =
    optionId === EVERYONE_OPTION_ID
      ? { audience: "circle", targetSubcircle: "" }
      : { audience: "subcircle", targetSubcircle: optionId };

  if (includeStaffOnly) {
    return { staffOnly: false, ...audiencePatch };
  }
  return audiencePatch;
}

export function writeNoticeAudiencePatch(
  patch: Record<string, unknown>,
  applyFormPatch?: (patch: Record<string, unknown>) => void,
  handleChange?: FormChangeHandler,
): void {
  if (applyFormPatch) {
    applyFormPatch(patch);
    return;
  }
  if (!handleChange) return;
  for (const [name, value] of Object.entries(patch)) {
    handleChange({ target: { name, value } });
  }
}

export function settleNoticeAudience(
  mode: NoticeAudienceSettleMode,
  catalog: NoticeAudienceCatalog,
  wire: NoticeAudienceWire,
  extra?: { isDirect?: boolean },
): NoticeAudienceWire | null {
  if (mode === "review") {
    if (extra?.isDirect) return null;
    if (wire.audience === "circle" && !catalog.canChooseEveryone) {
      if (catalog.circles.length > 0) {
        return {
          audience: "subcircle",
          targetSubcircle: catalog.circles[0].circleId,
        };
      }
      return { audience: "", targetSubcircle: "" };
    }
    return null;
  }

  if (catalog.canChooseEveryone || catalog.circles.length === 0) {
    return null;
  }

  if (mode === "mobileInfo") {
    if (
      !wire.targetSubcircle &&
      (!wire.audience || wire.audience === "")
    ) {
      return {
        audience: "subcircle",
        targetSubcircle: catalog.circles[0].circleId,
      };
    }
    return null;
  }

  if (!wire.targetSubcircle) {
    return {
      audience: "subcircle",
      targetSubcircle: catalog.circles[0].circleId,
    };
  }
  return null;
}

export function reviewStaffOnlyClearPatch(
  isEventNotice: boolean,
  staffOnly?: boolean,
): { staffOnly: false } | null {
  if (!isEventNotice && staffOnly) {
    return { staffOnly: false };
  }
  return null;
}
